/**
 * Smart Asset Composition Wizard
 *
 * Modal wizard for creating composite Smart Assets from primitives.
 * 5-step flow: Select -> Wire -> Gates -> Preview -> Create
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/blob/master/docs/SmartAssetSpec/SA-SPEC-007-Composition.md
 */
import type React from "react";
import type { CompositionWizardProps } from "./types";
import { CompositionWizardErrorBoundary } from "./ErrorBoundary";
import { CompositionWizardContent } from "./CompositionWizardContent";

/**
 * Composition Wizard Modal
 */
export const CompositionWizard: React.FC<CompositionWizardProps> = ({
  availableAssets,
  onClose,
  onComplete,
}) => {
  // Guard: Check for valid assets array
  if (!availableAssets || availableAssets.length === 0) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        data-testid="composition-wizard-empty"
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
          onKeyDown={(e) => e.key === "Escape" && onClose()}
          role="button"
          tabIndex={0}
          aria-label="Close wizard"
        />
        <div className="relative bg-sa-surface rounded-xl shadow-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sa-warning/10 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-sa-warning"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-sa-text mb-2">No Smart Assets Available</h3>
          <p className="text-sm text-sa-text-muted mb-6">
            You need at least 2 Smart Assets to create a composition. Add some Smart Assets first,
            then return to the composer.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <CompositionWizardErrorBoundary onClose={onClose}>
      <CompositionWizardContent
        availableAssets={availableAssets}
        onClose={onClose}
        onComplete={onComplete}
      />
    </CompositionWizardErrorBoundary>
  );
};

export default CompositionWizard;
